import React from 'react'
import { Link } from 'gatsby'
import { HeaderSubtitleCentered, SEO } from 'newComponents'
import Layout from '../components/layout'
import ClosingCTAs from '../components/ClosingCTAs'
import TabsAboutUs from '../components/about-us/tabsAboutUs'
import TabsTeam from '../components/about-us/tabsTeam'
import '../styles/about.scss'

import blueQuotes from '../images/about-us/Quotes_v2_Blue.svg'
import circle from '../images/pricing/CircleCI@2x.png'
import bessemer from '../images/about-us/investors/Bessemer.png'
import redpoint from '../images/about-us/investors/Redpoint.jpg'
import vertex from '../images/about-us/investors/Vertex.jpg'
import threshold from '../images/about-us/investors/Threshold.svg'
import uncork from '../images/about-us/investors/Uncork.jpg'
import beta from '../images/about-us/investors/BloombergBETA.jpg'
import fiveHund from '../images/about-us/investors/500.jpg'
import cervin from '../images/about-us/investors/Cervin@.jpg'

const AboutUs = () => (
  <Layout>
    <SEO
      title="Feature Flag Management Platform | About Us"
      description="Learn how teams use LaunchDarkly feature flag mangement platform to innovate faster, reduce risk, and break down barriers througout the organization."
    />
    <section className="section is-large bg-image about-us">
      <div className="container content hero">
        <div className="columns is-centered">
          <div className="column has-text-centered-tablet is-8">
            <h1 className="title">Feature Management for Modern Development</h1>
            <p className="text-large">
              Teams use LaunchDarkly to innovate faster, reduce risk, and break
              down the barriers between developer, product, marketing and sales.
            </p>
          </div>
        </div>
      </div>
    </section>

    <section className="section">
      <div className="container content">
        <div className="columns is-centered">
          <div className="column is-8">
            <HeaderSubtitleCentered
              header=" The Feature Management Platform Powering the Best Software
                Products"
            />
            <p className="text-box u-margintop-medium">
              Our vision is to eliminate risk for developers and operations
              teams from the software development cycle. As companies transition
              to a world built on software, there is an increasing requirement
              to move quickly—but that often comes with the desire to maintain
              control. LaunchDarkly is the feature management platform that
              enables dev and ops teams to control the whole feature lifecycle,
              from concept to launch to value.
            </p>
            <p>
              Feature flagging is an industry best practice of wrapping a new or
              risky section of code or infrastructure change with a flag. Each
              flag can easily be turned on/off independent of code deployment
              (aka ”dark launching”).
            </p>
            <p>
              Equipping businesses with the ability to move at the speed of
              every deploy allows an entire company to learn rapidly, deliver
              value to their customers faster, and produce more value.
              Developers can build, marketing can launch, product can iterate,
              and sales can sell.
            </p>
            <br />
          </div>
        </div>
        <div className="columns is-centered is-mobile is-multiline has-text-centered-tablet">
          <div className="column is-narrow">
            <div className="info-card">
              <div className="info">Founded</div>
              <div className="focus reduced-focus">2014</div>
              <svg>
                <line x1="0" y1="0" x2="100%" y2="0" />
              </svg>
              <div className="info">
                Serving trillions of feature flags daily.
              </div>
            </div>
          </div>
          <div className="column is-narrow">
            <div className="info-card">
              <div className="info">Team</div>
              <div className="focus reduced-focus">200+ Employees</div>
              <svg>
                <line x1="0" y1="0" x2="100%" y2="0" />
              </svg>
              <div className="info">
                Our global team is HQ'd in Oakland, CA.
              </div>
            </div>
          </div>
          <div className="column is-narrow">
            <div className="info-card">
              <div className="info">Raised</div>
              <div className="focus reduced-focus">$130M</div>
              <svg>
                <line x1="0" y1="0" x2="100%" y2="0" />
              </svg>
              <div className="info">
                In Series A, B &amp; C from trusted investors.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
    <section className="section is-large bg-static-stars bg-dark">
      <div className="container content">
        <div className="columns is-centered is-multiline">
          <div className="column is-12 has-text-centered-tablet">
            <img className="blockquote-logo" src={circle} alt="CircleCI logo" />
          </div>
          <div className="column is-1-desktop is-1-tablet is-12-mobile">
            <img className="icon righted" src={blueQuotes} alt="quotes" />
          </div>
          <div className="column is-7-desktop is-8-tablet is-12-mobile">
            <h2 className="title">
              I believe that feature flags are a critical piece of modern
              development, and LaunchDarkly brings this part of continuous
              delivery to the masses.
            </h2>
            <svg className="stroke-10">
              <line x1="0" y1="0" x2="100%" y2="0" />
            </svg>
            <p className="text-large">Rob Zuber, CTO, CircleCI</p>
          </div>
        </div>
      </div>
    </section>

    <TabsAboutUs />

    <TabsTeam />

    <section className="section is-large">
      <div className="container content">
        <div className="columns is-centered">
          <div className="column has-text-centered-tablet">
            <HeaderSubtitleCentered
              header="Our Investors"
              subtitle="People we're proud to partner with."
            />
            <div className="investors u-margintop-medium">
              <a
                href="https://www.bvp.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  className="large-investor-img"
                  src={bessemer}
                  alt="Bessemer logo"
                />
              </a>
              <a
                href="https://www.redpoint.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={redpoint} alt="Redpoint logo" />
              </a>
              <a
                href="http://www.vertexventures.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  className="large-investor-img"
                  src={vertex}
                  alt="Vertex logo"
                />
              </a>
              <a
                href="https://threshold.vc/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={threshold} alt="Threshold logo" />
              </a>
              <a
                href="https://uncorkcapital.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  className="large-investor-img"
                  src={uncork}
                  alt="Uncork logo"
                />
              </a>
              <a
                href="https://www.bloomberg.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={beta} alt="Bloomberg Beta logo" />
              </a>
              <a
                href="http://500.co/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img
                  className="large-investor-img"
                  src={fiveHund}
                  alt="500 Startups logo"
                />
              </a>
              <a
                href="http://www.cervinventures.com/"
                target="_blank"
                rel="noopener noreferrer"
              >
                <img src={cervin} alt="Cervin logo" />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <div className="bg-gradient">
      <section className="section is-large bridge-bg">
        <div className="container content">
          <div className="columns">
            <div className="column is-6">
              <h2>In the Community</h2>
              <p>
                LaunchDarkly is proud to call Oakland home. Though our team
                members come from all parts of the globe, our headquarters are
                in beautiful downtown Oakland. We think diversity is important
                and work hard to promote a safe and inclusive space for
                everyone. You can check out our code of conduct{' '}
                <Link to="/policies/code-of-conduct/">here</Link>.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>

    <ClosingCTAs />
  </Layout>
)

export default AboutUs
